// components/ChoreToolbar.tsx
import React from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon, TagIcon } from '@heroicons/react/24/outline';
import { ChoreView, NO_LABEL_FILTER, ScheduleKind, URGENCY_LABELS, Urgency } from '../domain';
import { Button, Chip, Field, Input, SegmentedControl, Select, cx } from './ui';

/** More households than this and the switcher becomes a select, so the toolbar doesn't wrap. */
const MAX_SEGMENTS = 3;

interface ChoreToolbarProps {
  households: { household: { id: string; name: string } }[];
  availableAssignees: { id: string; name: string }[];
  availableLabels: string[];
  availableScheduleKinds: { value: ScheduleKind; label: string }[];
  view: ChoreView;
  /** "Mine / Everyone" and the assignee filter; left out for a child, who has only their own chores. */
  showWhose: boolean;
  filters: {
    selectedHousehold: string;
    selectedAssignee: string;
    selectedLabels: string[];
    selectedScheduleKind: ScheduleKind | '';
    searchText: string;
    selectedUrgency: Urgency | '';
  };
  selectedAssigneeName: string;
  selectedScheduleLabel: string;
  panelFilterCount: number;
  hasFilters: boolean;
  setHousehold: (id: string) => void;
  setView: (view: ChoreView) => void;
  setAssignee: (id: string) => void;
  toggleLabel: (label: string) => void;
  setScheduleKind: (kind: ScheduleKind | '') => void;
  setSearchText: (text: string) => void;
  toggleUrgency: (urgency: Urgency) => void;
  clearFilters: () => void;
}

const labelText = (label: string) => (label === NO_LABEL_FILTER ? 'No label' : label);

/** Search, the household switcher, the rest of the filters behind one button, and what's active. */
export const ChoreToolbar: React.FC<ChoreToolbarProps> = (props) => {
  const { households, filters } = props;

  const householdSwitcher = households.length > 1 && (
    households.length > MAX_SEGMENTS ? (
      <Select
        compact
        aria-label="Household"
        value={filters.selectedHousehold}
        onChange={(e) => props.setHousehold(e.target.value)}
        className="w-auto"
      >
        <option value="">All households</option>
        {households.map(h => <option key={h.household.id} value={h.household.id}>{h.household.name}</option>)}
      </Select>
    ) : (
      <SegmentedControl
        label="Household"
        value={filters.selectedHousehold}
        onChange={props.setHousehold}
        options={[{ value: '', label: 'All' }, ...households.map(h => ({ value: h.household.id, label: h.household.name }))]}
      />
    )
  );

  const active: { key: string; text: string; remove: () => void }[] = [
    ...(filters.selectedUrgency ? [{ key: 'urgency', text: URGENCY_LABELS[filters.selectedUrgency], remove: () => props.toggleUrgency(filters.selectedUrgency as Urgency) }] : []),
    ...(filters.selectedAssignee ? [{ key: 'assignee', text: `Assigned to ${props.selectedAssigneeName}`, remove: () => props.setAssignee('') }] : []),
    ...(filters.selectedScheduleKind ? [{ key: 'schedule', text: props.selectedScheduleLabel, remove: () => props.setScheduleKind('') }] : []),
  ];

  return (
    <div className="space-y-3">
      {/* The labels sit out in the open, as the filter used most; selected ones show as selected
          here rather than again among the active filters. On a phone the row scrolls
          sideways instead of wrapping onto lines of its own. */}
      {props.availableLabels.length > 0 && (
        <div
          role="group"
          aria-label="Filter by label"
          className="-mx-4 flex items-center gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
        >
          <TagIcon className="h-4 w-4 flex-none text-gray-400 dark:text-gray-500" aria-hidden="true" />
          {[NO_LABEL_FILTER, ...props.availableLabels].map(label => (
            <Chip
              key={label}
              selected={filters.selectedLabels.includes(label)}
              onClick={() => props.toggleLabel(label)}
              className={cx('flex-none', label === NO_LABEL_FILTER && 'italic')}
            >
              {labelText(label)}
            </Chip>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Search chores"
            value={filters.searchText}
            onChange={(e) => props.setSearchText(e.target.value)}
            placeholder="Search chores, notes or labels"
            className="pl-9"
          />
        </div>

        {props.showWhose && (
          <SegmentedControl
            label="Whose chores"
            value={props.view}
            onChange={props.setView}
            options={[{ value: 'mine', label: 'Mine' }, { value: 'everyone', label: 'Everyone' }]}
          />
        )}

        {householdSwitcher}

        <Popover className="relative">
          <PopoverButton as={Button} icon={AdjustmentsHorizontalIcon}>
            Filters
            {props.panelFilterCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1.5 text-xs font-semibold text-white tabular-nums">
                {props.panelFilterCount}
              </span>
            )}
          </PopoverButton>
          <PopoverPanel
            anchor="bottom start"
            transition
            className={cx(
              'z-40 w-80 max-w-[calc(100vw-2rem)] space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-800 dark:bg-gray-900 dark:shadow-none [--anchor-gap:6px] focus:outline-none',
              'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 motion-reduce:transition-none',
            )}
          >
            {props.showWhose && (
              <Field label="Assigned to" htmlFor="chore-filter-assignee">
                <Select id="chore-filter-assignee" value={filters.selectedAssignee} onChange={(e) => props.setAssignee(e.target.value)}>
                  <option value="">Anyone</option>
                  {props.availableAssignees.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                </Select>
              </Field>
            )}

            <Field label="Repeats" htmlFor="chore-filter-schedule">
              <Select
                id="chore-filter-schedule"
                value={filters.selectedScheduleKind}
                onChange={(e) => props.setScheduleKind(e.target.value as ScheduleKind | '')}
              >
                <option value="">Any schedule</option>
                {props.availableScheduleKinds.map(kind => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
              </Select>
            </Field>

          </PopoverPanel>
        </Popover>
      </div>

      {(active.length > 0 || props.hasFilters) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {active.map(filter => (
            <Chip key={filter.key} selected onRemove={filter.remove} removeLabel={`Stop filtering by ${filter.text}`}>
              {filter.text}
            </Chip>
          ))}
          {props.hasFilters && (
            <Button variant="ghost" size="sm" onClick={props.clearFilters}>Clear all</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChoreToolbar;
